/**
 * TanStack Query hook for the surfacer endpoint.
 *
 * Reads from `/api/reader/cohort/<cohort>/study/<uid>/surfacer`, which
 * server-side filters a study's full reporter comparison_matrix down to
 * clinically significant missed findings (`acute` / `prompt_workup`
 * categories, `ai_only` at the per-finding recommended OP — meaning the AI
 * fired at the recommended bar or stricter). Each candidate carries the OP
 * advancement (how many OP steps stricter than recommended the AI fired) and
 * a cohort-relative percentile (cohort-wide rank of `probability` among
 * other flagged occurrences of the same finding). Probability surfaces as a
 * `High` / `Medium` confidence chip.
 *
 * cohort_id is supplied by the worklist row when available. Job-id parsing is
 * only a compatibility fallback because the image inference job and the
 * comparison review packet can belong to different cohorts.
 * Returns null (not error) when the upstream comparison row is missing.
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import type { OperatingPoint } from "./useStudyQueries";
import type { Verdict } from "./useSurfacerDecisions";
import { resolveReporterCohortId } from "./useReporterComparisonQueries";

/**
 * Adjudicator prediction (device-engine reporter >= a2z-reporter-v0.7.1) for a
 * surfaced finding. `report_mentions`/`no_report_coverage` are the down-rank
 * signals (predicted noise); `surface` means leave it prominent; null when the
 * row predates the adjudicator — also rendered prominent.
 */
export type AdjudicationVerdict = "report_mentions" | "no_report_coverage" | "surface";

/**
 * True when the adjudicator predicts a surfaced finding is noise (already in the
 * report, or in a region the report on file doesn't cover) and should be
 * down-ranked. `surface`/null stay prominent. SSoT for the down-rank predicate
 * shared by SurfacerPanel and the worklist RetroAgreementStage.
 */
export function isPredictedNoise(verdict: AdjudicationVerdict | null): boolean {
  return verdict === "report_mentions" || verdict === "no_report_coverage";
}

export interface AdjudicationChip {
  /** Short lowercase chip label. */
  label: string;
  /** Hover title explaining why the finding was down-ranked. */
  title: string;
}

/**
 * Chip describing the down-rank reason, or null when the verdict isn't a noise
 * signal. SSoT for the chip vocabulary so SurfacerPanel and RetroAgreementStage
 * can't drift on wording.
 */
export function adjudicationChip(
  verdict: AdjudicationVerdict | null,
  evidenceQuote: string | null,
): AdjudicationChip | null {
  if (verdict === "report_mentions") {
    return {
      label: "in report ⓘ",
      title: evidenceQuote
        ? `Report appears to mention this: "${evidenceQuote}"`
        : "Report appears to mention this finding",
    };
  }
  if (verdict === "no_report_coverage") {
    return {
      label: "not covered",
      title: "Report on file doesn't cover this finding's region (separate report?)",
    };
  }
  return null;
}

export interface SurfacerCandidate {
  finding: string;
  display_name: string;
  anatomy: string;
  // The surfacer only surfaces its billable clinical tiers (device-engine
  // clinical_tier `acute` / `prompt_workup`). Exact mirror of the demo-backend
  // surfacer CATEGORIES_BILLABLE — the pre-2.0.0 `high_priority`/`actionable`
  // aliases are gone on both sides (every reporter >= v0.8.1 emits clinical_tier
  // names; legacy-name cohorts are retired from a2z-reader-derived).
  category: "acute" | "prompt_workup";
  /** Null when the device reports no probability (it fires per operating point only). */
  confidence: "High" | "Medium" | null;
  recommended_op: OperatingPoint;
  fired_op: OperatingPoint;
  advancement: number;
  percentile: number | null;
  top_slice: string | null;
  report_excerpt: string;
  /** Section header the heuristic anchored to; null for a device quote. */
  report_anchor: string | null;
  /**
   * Where `report_excerpt` came from: the device's own quote (`adjudicator` —
   * the verified evidence quote — or `matcher`), the backend's keyword
   * `heuristic` (nearest section, a guess), or null when it is empty. Absent on
   * a backend older than the field, which only had the heuristic.
   */
  report_excerpt_source?: "adjudicator" | "matcher" | "heuristic" | null;
  miss_phrase: string;
  predicted_verdict: AdjudicationVerdict | null;
  adjudication_reason: string | null;
  evidence_quote: string | null;
  /**
   * A verdict covers this study+finding: the caller's own in this cohort, or a
   * reused one (`prior_verdict`). Absent on a backend older than verdict reuse
   * across a version family (web-demo-backend #792).
   */
  judged?: boolean;
  /**
   * The latest verdict on this study+finding from any cohort in this cohort's
   * version family, when the caller has none in THIS cohort. Read-only: a
   * verdict clicked here is still written to this cohort as the caller's own.
   */
  prior_verdict?: PriorVerdict | null;
}

/**
 * A verdict reused from the version family: same study, same finding, given by
 * some reviewer in some family cohort. Internal staff see every reviewer's;
 * external reviewers only their own (the backend filters).
 */
export interface PriorVerdict {
  verdict: Exclude<Verdict, "pending">;
  /** Reviewer email. */
  reviewer: string;
  /** The version (replay cohort) the verdict was given under. */
  cohort_id: string;
  timestamp: string;
  actionable?: boolean;
  note?: string;
}

/**
 * One version of this study: a family cohort (a report-matching replay over the
 * same image predictions) that holds the study, with the study's job id there.
 */
export interface SurfacerVersion {
  cohort_id: string;
  /** This study's job id in that cohort — the case route's `<uid>::<job_id>`. */
  job_id: string;
  /** Report-model tag of that cohort's row; null when the row has none. */
  model: string | null;
}

/** Judged/unjudged tally for one study, pooled across the version family. */
export interface SurfacerReviewState {
  /** This cohort plus the family cohorts the caller can see that hold a verdict
   * on this study. More than one means other versions of this study exist. */
  pooled_cohort_ids: string[];
  candidates: number;
  judged: number;
  unjudged: number;
}

/**
 * A finding the AI fired on that the report DOES mention — the agreed set.
 *
 * Deliberately thinner than `SurfacerCandidate`: no confidence band, no OP
 * advancement, no percentile. A match asks one question ("is this match right?")
 * and those fields only invite re-litigating the surfacing decision.
 */
export interface SurfacerMatch {
  finding: string;
  display_name: string;
  anatomy: string;
  category: SurfacerCandidate["category"];
  /** The report excerpt the comparator matched on — the basis of the match. */
  matched_quote: string | null;
  top_slice: string | null;
}

export interface SurfacerResponse {
  study_uid: string;
  cohort_id: string;
  original_report_text: string;
  coverage_gap: boolean;
  uncovered_findings: string[];
  candidates: SurfacerCandidate[];
  /** Absent on responses from a backend older than the matched-section change. */
  matched?: SurfacerMatch[];
  /**
   * The probability at or above which `confidence` is "High" (backend
   * `CONF_HIGH_MIN`). Sent so the UI can explain the band without restating the
   * number — a hardcoded 0.55 drifts silently if that constant moves. Absent on
   * an older backend, in which case the copy omits the figure.
   */
  confidence_threshold?: number;
  /** Absent on a backend older than verdict reuse (web-demo-backend #792). */
  review_state?: SurfacerReviewState;
  /**
   * Every version of this study the caller can see, judged or not, oldest first,
   * the current cohort included (web-demo-backend #793). Feeds the Version
   * picker; absent on an older backend, which then shows no picker.
   */
  versions?: SurfacerVersion[];
}

export const surfacerKeys = {
  all: ["surfacer"] as const,
  study: (studyUid: string, cohortId: string) =>
    [...surfacerKeys.all, "study", cohortId, studyUid] as const,
};

async function fetchSurfacer(cohortId: string, studyUid: string): Promise<SurfacerResponse | null> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  try {
    const response = await axios.get<SurfacerResponse>(
      `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/surfacer`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export function useSurfacerCandidates(
  studyUid: string | null,
  jobId: string | null,
  readerCohortId?: string | null,
) {
  const cohortId =
    studyUid && jobId ? resolveReporterCohortId(studyUid, jobId, readerCohortId) : null;
  return useQuery({
    queryKey: surfacerKeys.study(studyUid ?? "", cohortId ?? ""),
    queryFn: () => fetchSurfacer(cohortId!, studyUid!),
    enabled: !!studyUid && !!cohortId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount) => failureCount < 1,
  });
}
